import { PropsWithChildren, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X as CloseIcon, Ellipsis as AltMenuIcon } from 'lucide-react';

import { paths } from 'configuration/paths';
import { classNameList } from 'utils/property';
import { smoothScrollToTop } from 'utils/scroll';
import { TranslationsKeys } from 'types/translation';
import { NavigationItem } from 'types/navigation';
import { useAuthentication } from 'hooks/useAuthentication';
import { useTranslate } from 'hooks/useTranslate';
import { Spinner } from 'components/Spinner';
import { HEADER_MOBILE_HEIGHT } from './constants';
import { useNavigationClassNameList } from './hooks/useNavigationClassNameList';
import { useNavigationItems } from './hooks/useNavigationItems';
import { UserMenu } from './components';
import styles from './Header.module.scss';

interface IProps {
  navigationItems?: NavigationItem[];
  ignoreAuthError?: boolean;
}

export const Header = ({ children, navigationItems, ignoreAuthError }: PropsWithChildren<IProps>): JSX.Element => {
  const navigationOffsetY = 0;
  const navigationRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { user, loading: loadingAuthentication, error: errorAuthentication } = useAuthentication();
  const { t } = useTranslate();
  const { navigationItems: defaultNavigationItems } = useNavigationItems();
  if (!navigationItems) {
    navigationItems = defaultNavigationItems;
  }
  const [menuTouched, setMenuTouched] = useState<boolean>(false);
  const [menuExpanded, setMenuExpanded] = useState<boolean>(false);
  const navClassNameList = useNavigationClassNameList(navigationOffsetY, navigationRef);

  const expandMenu = useCallback(async () => {
    setMenuTouched(true);
    const headerVisible = window.scrollY < navigationOffsetY + HEADER_MOBILE_HEIGHT * 2;
    if (headerVisible) {
      await smoothScrollToTop();
    }

    setMenuExpanded(true);
  }, [navigationOffsetY]);

  useEffect(() => {
    if (menuExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [menuExpanded]);

  const collapseMenu = useCallback((): void => {
    setMenuExpanded(false);
  }, []);

  return (
    <header role="banner" className={styles.header}>
      <div className={styles.mobileNavContainer}>
        <nav
          ref={navigationRef}
          aria-expanded={menuExpanded}
          aria-label={t(TranslationsKeys.mainNavigationAriaLabel)}
          className={navClassNameList}>
          <div>
            {children || (
              <Link to={paths.root}>
                <div className={styles.headerLogo}></div>
              </Link>
            )}
            {errorAuthentication && !ignoreAuthError && (
              <div className="m-2 fs-6 text-danger">{t(TranslationsKeys.authenticationContext_error)}</div>
            )}
            {!loadingAuthentication && user?.loggedIn && (!children || navigationItems.length > 0) && (
              <div className={styles.sideFunctionBoxes}>
                {!children && <UserMenu className={styles.sideFunctionBox} />}
                {navigationItems.length > 0 && (
                  <div className={styles.sideFunctionBox}>
                    <button
                      id="expandMenuButton"
                      aria-label={
                        menuExpanded
                          ? t(TranslationsKeys.collapseNavigationAriaLabel)
                          : t(TranslationsKeys.expandNavigationAriaLabel)
                      }
                      aria-haspopup={menuExpanded ? 'false' : 'true'}
                      aria-expanded={menuExpanded}
                      aria-controls="expandedMenuArea"
                      onClick={menuExpanded ? collapseMenu : expandMenu}>
                      {menuExpanded && <CloseIcon className={styles.headerIcon} />}
                      {!menuExpanded && !children && <MenuIcon className={styles.headerIcon} />}
                      {!menuExpanded && children && <AltMenuIcon className={styles.headerIcon} />}
                    </button>
                  </div>
                )}
                {location.pathname !== paths.login && loadingAuthentication && (
                  <div className={classNameList('px-2', styles.sideFunctionBox)}>
                    <Spinner />
                  </div>
                )}
              </div>
            )}
          </div>
          {!loadingAuthentication && user?.loggedIn && (
            <ul
              id="expandedMenuArea"
              role="menu"
              aria-hidden={!menuExpanded}
              aria-label={t(TranslationsKeys.expandNavigationAriaLabel)}
              className={classNameList(styles.menuExpansion, menuTouched && styles.menuExpansionTouched)}>
              <li role="none" className={styles.menuExpansionItem}>
                <ul
                  id="mainNavigationLinks"
                  role="menu"
                  aria-label={t(TranslationsKeys.mainNavigationLinksLabel)}
                  className={styles.mainNavigationLinks}>
                  {navigationItems.map((item: NavigationItem) => (
                    <li
                      role="none"
                      key={item.text}
                      className={classNameList(
                        styles.mainNavigationLink,
                        item.sticky && styles.mainNavigationLinkBottom
                      )}>
                      {item.url ? (
                        <Link
                          to={item.url}
                          onClick={item.onClick}
                          role="menuitem"
                          className={styles.linkContent}
                          aria-current={item.selected ? 'page' : false}
                          tabIndex={menuExpanded ? undefined : -1}>
                          <div className={styles.linkContent}>
                            {item.icon && <item.icon className={styles.linkContentIcon} />}
                            <div>{item.text}</div>
                          </div>
                        </Link>
                      ) : (
                        <button
                          onClick={item.onClick}
                          type="button"
                          role="menuitem"
                          className={styles.linkContent}
                          aria-current={item.selected ? 'page' : false}
                          tabIndex={menuExpanded ? undefined : -1}>
                          <div className={styles.linkContent}>
                            {item.icon && <item.icon className={styles.linkContentIcon} />}
                            <div>{item.text}</div>
                          </div>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          )}
        </nav>
        <div className={styles.reservedSpace} aria-hidden={true} />
      </div>
    </header>
  );
};
